/* Purpose: This is the central module that puts all the pages of the app together. 
 *          For logged in users, routes are configured here. For guest users, a basic home-style page is rendered here.
 * Props: GlobalVar (map)
 * Author: R. Markowitz (April 2025)
 * Dev Notes: 
*/

import {React, useState, useEffect, useMemo} from 'react';
import { BrowserRouter as Router, Route, Routes} from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './custom.scss'
import {useAuth } from 'react-oidc-context';
import AWS from 'aws-sdk';
import Home from './Pages/Home';
import GuestHome from './Pages/GuestHome';
import PrivacyPolicy from './Pages/Privacy-Policy';
import Portal from './Pages/Portal';
import Layout from './Layout/Layout';
import Footer from './Layout/Footer';
import AdminLayout from './Layout/AdminLayout';
import GuestLayout from './Layout/GuestLayout';
import General from './Pages/Portal/General';
import Loans from './Pages/Portal/Loans';
import Review from './Pages/Portal/Review';
import ClientList from './Pages/Admin/ClientList';
import Error from './Components/Error';
import Loading from './Components/Loading';
import SubmitButton from './Components/Button';
import {Row, Container, Button} from 'react-bootstrap';
import SloganHeader from './Components/SloganHeader';
import HomeInfo from './Components/HomeInfo';
import { PolicyNotFoundException } from '@aws-sdk/client-dynamodb';


const App = ({GlobalVar}) => {
    const auth = useAuth();
    const [credentials, setCredentials] = useState();
    // const [isAdmin, setIsAdmin] = useState("");
    const isAdmin = useMemo(() => {
        return(auth.user?.profile["cognito:groups"][0] == "Admin")
    },[auth.user])

    
    AWS.config.region = GlobalVar.REGION;

    // When a user signs into cognito and auth.user is set, 
    // finish the sign in process by checking if user is admin and setting all user's permissions
    useEffect(() =>{
        const finishSignIn = async() => {
            const params = {
                "method":"POST",
                "body":JSON.stringify({
                    "UserPoolID": GlobalVar.USER_POOL_ID,
                    "IdentityPoolID": GlobalVar.IDENTITY_POOL_ID,
                    "jwt_token": auth.user.id_token
                })
            }

            try{
                const response = await fetch("https://f0ll5x1c4l.execute-api.us-east-1.amazonaws.com/setClientPermissions", params);
                if (response.ok){
                    const authData = await response.json();
                    setCredentials(authData.credentials)
                }else{
                    console.error("Error occured while authorizing this user.")
                }
            }catch (e){
                console.error("Error occured while authorizing this user. ", e)
            }
        };
        const currentTime = new Date()
        if (auth.user && new Date(auth.user.expires_at * 1000) > currentTime){
          finishSignIn(); 
        }else if (auth.user && new Date(auth.user.expires_at * 1000) < currentTime ){
            sessionStorage.clear()
        }
        
    }, [auth.user])   

    // To sign user out of cognito
    const signOutRedirect = () => {
        auth.removeUser()
        const clientId = GlobalVar.APP_CLIENT_ID;
        const logoutUri = GlobalVar.HOME_URL;
        const cognitoDomain = GlobalVar.COGNITO_DOMAIN;
        window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
      };

    if (auth.isLoading){
        return(
            <Loading />
        )
    }

    // If there is an error signing the user in or with the user's token/credentials, 
    // First try reloading the sign in page,
    // If error persists, raise error
    if (auth.error){
        try{
            auth.signinRedirect();
        }catch(e){
            console.log("Attempted to sign in again. Attempt failed.")
        }
        return (<Error 
                    statusCode = "500" 
                    message = "An Error Occured While Retrieving Your Credentials" 
                    error = {auth.error.message} 
                    stackTrace = {auth.error.stack} 
                />)
    }
 
    return (
        <div className='app-div'>
            {auth.isAuthenticated? (
                <>
                {credentials?(
                    <>
                        <div className='content'>
            
                            <Router className = 'router' onUpdate={() => window.scrollTo(0, 0)}>

                                {isAdmin != "" && isAdmin? (
                                    <div>
                                        <AdminLayout />
                                    </div>
                                ):(
                                    <div>  
                                        <Layout />
                                    </div> 
                                )}

                                <Routes>
                                    <Route path="/"
                                        element = {<Home GlobalVar={GlobalVar} isAdmin = {isAdmin}/>} 
                                    />
                                    <Route path = "/Privacy-Policy"
                                        element = {<PrivacyPolicy GlobalVar={GlobalVar}/>} 

                                    />
                                    <Route path="/Portal" 
                                        element ={isAdmin != "" && isAdmin?(
                                            <Error statusCode = "403" message = "Unauthorized Request" error = "Unauthorized. The logged in user does not have access to the requested page." stackTrace = {"isAdmin = " + isAdmin}/>
                                            ):(
                                            <Portal GlobalVar = {GlobalVar}/>
                                        )}
                                    />
                                    <Route path="/Portal/General" 
                                        element = {isAdmin?(
                                            <Error statusCode = "403" message = "Unauthorized Request" error = "Unauthorized. The logged in user does not have access to the requested page." />
                                            ):(
                                            <General ClientID = {auth.user?.profile['sub']} credentials = {credentials}/>
                                            )} 
                                    />
                                    <Route path="/Portal/Loans" 
                                        element = {isAdmin?(
                                            <Error statusCode = "403" message = "Unauthorized Request" error = "Unauthorized. The logged in user does not have access to the requested page." />
                                            ):(
                                            <Loans ClientID = {auth.user?.profile['sub']} credentials = {credentials}/>
                                            )} 
                                    />
  
                                    <Route path="/Portal/Review" element = {<Review ClientID = {auth.user?.profile['sub']}  credentials = {credentials}/>} />

                                    <Route path="/Admin"
                                        element = {isAdmin?(
                                            <ClientList AdminID = {auth.user?.profile['sub']} username = {auth.user?.profile['cognito:username']} GlobalVar = {GlobalVar} credentials = {credentials} />
                                            ):(
                                            <Error statusCode = "403" message = "Unauthorized Request" error = "Unauthorized. The logged in user does not have access to the requested page." />
                                            )} />
                                    <Route path='/Loading' element = {<Loading />} />
                                    {/* For any path that does not exist, show 404 error */}
                                    <Route path="*" element={<Error statusCode = "404" message = "Page Not Found" error = "The requested page was not found." />} /> {/* For any path that does not exist, show 404 error */}
                                </Routes>
                            </Router>
                        </div>
            
                        <Footer signOutRedirect = {signOutRedirect}/>
            
                        </> ):(
                        //If the user is authenticated but sign in is not yet finished
                        <Loading />
                        )}</>
            ):(
                <div className='w-100'>
                    <div className='content'>
                        <GuestLayout />

                        <Router className = 'router' onUpdate={() => window.scrollTo(0, 0)}>
                            <Routes>
                                <Route path = "/" element = {<GuestHome GlobalVar={GlobalVar} signinRedirect = {() => auth.signinRedirect()} />} />
                                <Route path = "/Privacy-Policy" element = {<PrivacyPolicy GlobalVar={GlobalVar} />} />
                            </Routes>
                        </Router>
                    </div>
                    <Footer />
                        

                </div>
            )}
            
        </div>
        
    );
};

export default App;
